import { Button } from "@/atoms-react/button/Button.react";
import InputLabel from "@/atoms-react/input/Input.react";
import { Base } from "@/atoms-react/text/Text.react";
import { useAdmin } from "@/features/admin/hooks/use-admin";
import { Themes } from "@/types/theme";
import {
  BuildingOffice2Icon,
  MagnifyingGlassIcon,
  PlusCircleIcon
} from "@heroicons/react/20/solid";
import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { LayoutTitle } from "../../_layout/index.react";
import { AddCompanyModal } from "../../company/components/AddCompanyModal";
import ListCompanies from "./ListCompanies";
import { Modal, ModalContent } from "@/atoms-react/modal/index.react";
import { Dropdown } from "@/atoms-react/dropdown/index.react";
import { CompanySituation } from "@/features/admin/types";
import { companyAdminNewListType } from "@/features/company/types";
import toast from "react-hot-toast";
import { Checkbox } from "@/atoms-react/input-checkbox/index.react";
import { useAppDispatch } from "@/redux/hooks";
import { ADMIN_SOURCE_PAGE, setAdminSourcePage } from "@/redux/slices/adminSlice";

export const AdminNew = () => {
  const { t } = useTranslation();
  const [addCompany, setAddCompany] = useState<boolean>(false);
  const [editCompany, setEditCompany] = useState<boolean>(false);
  const [situationEdit, setSituationEdit] = useState<string | null>();
  const [company, setCompany] = useState<companyAdminNewListType | null>(null);

  const [queryLeName, setQueryLeName] = useState<string | undefined>(undefined);
  const [queryLeNameDisplayed, setQueryLeNameDisplayed] = useState<string | undefined>(undefined);
  const [showAll, setShowAll] = useState<boolean>(false);

  const handleChangesQuery = useCallback(
    debounce((name: string) => {
      setQueryLeName(name);
    }, 1200),
    []
  );

  const open = () => setAddCompany(true);
  const close = () => setAddCompany(false);
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const { companiesAdminNewQuery, editCompanySituationMutation } = useAdmin();
  const { isPending, data, isFetching, isPlaceholderData, refetch } = companiesAdminNewQuery(
    showAll,
    page,
    limit,
    queryLeName
  );
  const { mutateAsync: editCompanySituation, isPending: loadingEdit } =
    editCompanySituationMutation();
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(setAdminSourcePage(ADMIN_SOURCE_PAGE.NEW));
  }, []);

  return (
    <div className="px-6">
      <AddCompanyModal closeCompanyModal={close} open={addCompany} />
      <Modal
        className="max-w-xl"
        open={editCompany}
        onClose={() => {
          setEditCompany(false);
          setSituationEdit(null);
          setCompany(null);
        }}
      >
        <ModalContent title={t("admin_page.title.update")}>
          <div className={"mt-4 opacity-1"}>
            <div className={"mt-4 mb-6 w-full"}>
              <>
                <Dropdown
                  value={situationEdit}
                  onChange={(item) => {
                    setSituationEdit(item.key);
                  }}
                  dataCy="admin-situation-dropodwn"
                  required={true}
                  className="w-full"
                  items={Object.values(CompanySituation).map((cs) => {
                    return {
                      key: cs,
                      value: t(`admin_page.table.${cs.toLocaleLowerCase()}`),
                      render: () => (
                        <Base dataCy={`situation-admin-${cs}`}>
                          {t(`admin_page.table.${cs.toLocaleLowerCase()}`)}
                        </Base>
                      )
                    };
                  })}
                />
              </>
            </div>
            <div className={"flex w-full flex-row justify-end"}>
              <Button
                size="md"
                disabled={!situationEdit}
                loading={loadingEdit}
                className="w-max"
                theme={Themes.primary}
                dataCy="admin-situation-update"
                onClick={async () => {
                  if (!situationEdit || !company) return;
                  try {
                    const resp = await editCompanySituation({
                      companyUuid: company?.uuid,
                      situation: situationEdit
                    });
                    const index = data?.companyList.findIndex(
                      (d) => d.uuidLegal == resp.data.uuidLegal
                    );
                    if (data)
                      if (index) {
                        data.companyList[index].situation = resp.data.situation;
                      } else {
                        await refetch();
                      }
                    setEditCompany(false);
                    setSituationEdit(null);
                    setCompany(null);
                    toast.success(t("olkypass.accountPage.notifications.success.profile.update"));
                  } catch (_error) {
                    toast.error(t("olkypass.accountPage.notifications.error.profile.update"));
                  }
                }}
              >
                {t("admin_page.button.update")}
              </Button>
            </div>
          </div>
        </ModalContent>
      </Modal>
      <LayoutTitle>{t("admin_page.menu.new")}</LayoutTitle>
      <div className="mt-10 flex flex-col justify-between md:flex-row">
        <div className="flex flex-col items-center justify-center">
          <div className="flex flex-row items-center">
            <BuildingOffice2Icon className="mr-2 h-5 w-5 dark:text-white" />
            <Base className="font-semibold text-lg"> {t("admin_page.title.subtitle")}</Base>
          </div>
        </div>
        <Button
          size="md"
          className="w-max"
          theme={Themes.primary}
          onClick={() => {
            open();
          }}
          iconPosition="after"
          icon={<PlusCircleIcon />}
        >
          {t("admin_page.button.create")}
        </Button>
      </div>
      <div className="py-10">
        <Base className="mb-2"> {t("admin_page.input_label.search")}</Base>
        <div className="mb-5 flex flex-row">
          <div className="w-full basis-128">
            <InputLabel
              value={queryLeNameDisplayed}
              placeholder={t("admin_page.input_placeholder.search")}
              suffixIcon={
                <div className="h-full">
                  <MagnifyingGlassIcon
                    onClick={async () => {
                      setPage(1);
                      await refetch();
                    }}
                    className="text-primary"
                  />
                </div>
              }
              onChange={(value) => {
                setPage(1);
                handleChangesQuery(value);
                setQueryLeNameDisplayed(value);
              }}
            />
          </div>
          <div className="ml-3 basis-64">
            <div className="flex h-full items-center justify-center">
              <Checkbox
                value={showAll}
                onChange={() => {
                  setShowAll(!showAll);
                  if (page !== 1) setPage(1);
                }}
              >
                <Base className="text-base"> {t("admin_page.filter.showAll")}</Base>
              </Checkbox>
            </div>
          </div>
        </div>

        <ListCompanies
          isPlaceholderData={isPlaceholderData}
          limit={limit}
          page={page}
          handleEdit={(company: companyAdminNewListType) => {
            setEditCompany(true);
            setCompany(company);
            if (company.situation) setSituationEdit(company.situation);
          }}
          handleLimit={(limit) => setLimit(limit)}
          handlePage={(page) => setPage(page)}
          isLoading={isPending || isFetching}
          listcompaniesResponse={data}
        />
      </div>
    </div>
  );
};
